import React, { useState } from "react";
import {
  ActionIcon,
  Badge,
  Collapse,
  Group,
  List,
  Menu,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconArchive,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconDotsVertical,
  IconPencil,
} from "@tabler/icons-react";
import { SalonService } from "../../../types/context";
import { ServicePackage } from "../../../types/packages";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import {
  formatQty,
  isPerUnitBasis,
  unitLabelForBasis,
} from "../../../utils/quantityUtils";
import { formatSpecialWindow } from "./specialDates";

interface PackageCardProps {
  pkg: ServicePackage;
  /** Used to resolve per-unit ("syringes"/"units") suffixes on item rows. */
  services: SalonService[];
  canEdit: boolean;
  /** Past specials render dimmed. */
  dimmed?: boolean;
  onEdit: (pkg: ServicePackage) => void;
  onDuplicate: (pkg: ServicePackage) => void;
  onArchive: (pkg: ServicePackage) => void;
}

/**
 * Full-width horizontal expandable card for a package or special. Shows the
 * name, badge row, and price up front; description and the item list live
 * in the collapse. Never shows the offer/template type.
 */
export default function PackageCard({
  pkg,
  services,
  canEdit,
  dimmed = false,
  onEdit,
  onDuplicate,
  onArchive,
}: PackageCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const itemLabel = (salonServiceId: string, quantity: number): string => {
    const service = services.find((svc) => svc.id === salonServiceId);
    const basis = service?.price_basis;
    const unit = isPerUnitBasis(basis)
      ? ` ${unitLabelForBasis(basis, quantity)}`
      : "";
    return `${formatQty(quantity)}${unit}`;
  };

  const badges = (
    <Group gap="xs" wrap="wrap">
      {pkg.kind === "special" && (
        <Badge size="sm" variant="light" color="burntOrange">
          {formatSpecialWindow(pkg)}
        </Badge>
      )}
      {pkg.is_purchasable_online && (
        <Badge size="sm" variant="light" color="var(--brand-teal)">
          Available for purchase on the Client Booking Platform
        </Badge>
      )}
      <Badge size="sm" variant="light" color="gray">
        {pkg.validity_days
          ? `Expires ${pkg.validity_days} days after purchase`
          : "Never expires"}
      </Badge>
      {pkg.omit_tax_collection && (
        <Badge size="sm" variant="light" color="teal">
          Tax included in price
        </Badge>
      )}
    </Group>
  );

  return (
    <Paper p="md" withBorder style={dimmed ? { opacity: 0.6 } : undefined}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={6} style={{ flex: 1 }}>
            <Text fw={600}>{pkg.name}</Text>
            {badges}
          </Stack>

          <Group gap="xs" wrap="nowrap">
            <Text fw={600} c="var(--brand-teal)">
              {formatCurrency(pkg.price)}
            </Text>
            {canEdit && (
              <Menu position="bottom-end" withinPortal shadow="md">
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={`Actions for ${pkg.name}`}
                  >
                    <IconDotsVertical size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconPencil size={14} />}
                    onClick={() => onEdit(pkg)}
                  >
                    Edit
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCopy size={14} />}
                    onClick={() => onDuplicate(pkg)}
                  >
                    Duplicate
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<IconArchive size={14} />}
                    onClick={() => onArchive(pkg)}
                  >
                    Archive
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={isExpanded ? "Collapse details" : "Expand details"}
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? (
                <IconChevronUp size={18} />
              ) : (
                <IconChevronDown size={18} />
              )}
            </ActionIcon>
          </Group>
        </Group>

        <Collapse in={isExpanded}>
          <Stack gap="sm" pt={4}>
            {pkg.description && (
              <Text size="sm" c="dimmed">
                {pkg.description}
              </Text>
            )}
            <List size="sm" spacing={2}>
              {pkg.items.map((item) => (
                <List.Item key={item.salon_service_id}>
                  {itemLabel(item.salon_service_id, item.quantity)} ×{" "}
                  {item.service_name}
                </List.Item>
              ))}
            </List>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
